import {Box, Grid, Heading, HStack, Icon, Text, ThemeIconKey, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import styled from 'styled-components';

import {ReactNode} from 'react';

import {PayPerUseMetricsResponse} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {aggregateOperationTypes} from '../utils/aggregateOperationTypes';
import {calculateAvgPerUser} from '../utils/calculateAvgPerUser';
import {calculateDelta} from '../utils/calculateDelta';
import {PayPerUseTrend} from './PayPerUseTrend';

const ICON_SIZE = 4;
const CARD_PADDING = 4;

// Highlighted "Total operations" tile: linear-gradient(135deg, #172B4D, #0747A6)
// via theme tokens (neutral.800 → blue.80) — no hardcoded hex.
const HighlightCard = styled.div`
  padding: ${({theme}) => theme.getSize(CARD_PADDING)};
  border-radius: ${({theme}) => theme.radii.small};
  background: linear-gradient(
    135deg,
    ${({theme}) => theme.colors.palettes.neutral[800][100]},
    ${({theme}) => theme.colors.palettes.blue[80][100]}
  );
`;

interface PayPerUseKpiHeaderProps extends RequiredTestIdProps {
  metrics: PayPerUseMetricsResponse;
}

interface KpiTile {
  id: string;
  icon: ThemeIconKey;
  label: string;
  value: string | null;
  footer: ReactNode;
  isHighlighted?: boolean;
}

export function PayPerUseKpiHeader(props: PayPerUseKpiHeaderProps) {
  const formatNumber = useFormatNumber();

  const totalDelta = calculateDelta(props.metrics.totalCount, props.metrics.previousTotalCount);
  const operationGroupCount = aggregateOperationTypes(props.metrics.operationTypes).length;

  const tiles: KpiTile[] = [
    {
      id: 'totalOperations',
      icon: 'action/offline_bolt',
      label: i18n.t('page.homepage.payPerUse.totalOperations'),
      value: formatNumber(props.metrics.totalCount),
      isHighlighted: true,
      footer: (
        <HStack spacing={2} align="center">
          <PayPerUseTrend
            delta={totalDelta}
            isOnDarkBackground
            data-testid={suffixTestId('kpi-totalOperations-trend', props)}
          />
          <Text size="small" color="white">
            {i18n.t('page.payPerUseMetrics.kpi.vsPreviousPeriod')}
          </Text>
        </HStack>
      ),
    },
    {
      id: 'operationTypes',
      icon: 'action/dashboard',
      label: i18n.t('page.payPerUseMetrics.kpi.operationTypes'),
      value: formatNumber(operationGroupCount),
      footer: (
        <Text size="small" color="tertiary">
          {i18n.t('page.payPerUseMetrics.kpi.trackedAndBillable')}
        </Text>
      ),
    },
    {
      id: 'activeUsers',
      icon: 'social/group',
      label: i18n.t('page.payPerUseMetrics.kpi.activeUsers'),
      value: formatNumber(props.metrics.activeUserCount),
      footer: (
        <Text size="small" color="tertiary">
          {i18n.t('page.payPerUseMetrics.kpi.activeThisPeriod')}
        </Text>
      ),
    },
    {
      id: 'avgPerUser',
      icon: 'action/trending_up',
      label: i18n.t('page.payPerUseMetrics.kpi.avgPerUser'),
      value: formatNumber(
        calculateAvgPerUser(props.metrics.totalCount, props.metrics.activeUserCount)
      ),
      footer: (
        <Text size="small" color="tertiary">
          {i18n.t('page.payPerUseMetrics.kpi.operationsThisPeriod')}
        </Text>
      ),
    },
  ];

  return (
    <Grid columns={[1, 2, 4]} spacing={4}>
      {tiles.map((tile) => {
        const content = (
          <VStack spacing={3}>
            <HStack spacing={2} align="center">
              <Icon
                value={tile.icon}
                size={ICON_SIZE}
                color={tile.isHighlighted ? 'text.white' : 'text.secondary'}
              />
              <Text size="small" color={tile.isHighlighted ? 'white' : 'secondary'}>
                {tile.label}
              </Text>
            </HStack>
            <Heading size={2} color={tile.isHighlighted ? 'white' : undefined}>
              {tile.value}
            </Heading>
            {tile.footer}
          </VStack>
        );

        return tile.isHighlighted ? (
          <HighlightCard key={tile.id} data-testid={suffixTestId(`kpi-${tile.id}`, props)}>
            {content}
          </HighlightCard>
        ) : (
          <Box
            key={tile.id}
            backgroundColor="general.white"
            border="1px solid"
            borderColor="general.separator"
            borderRadius="small"
            padding={CARD_PADDING}
            data-testid={suffixTestId(`kpi-${tile.id}`, props)}
          >
            {content}
          </Box>
        );
      })}
    </Grid>
  );
}
